import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import Card from '@/components/ui/Card';
import SlotProgress from '@/components/sponsor/SlotProgress';
import GiftingForm from '@/components/sponsor/GiftingForm';
import SponsorMarketplace from '@/pages/sponsor/dashboard/Marketplace';
import SponsorProfileDashboardProfile from '@/pages/sponsor/dashboard/profile';
import { SponsorSupportTab } from '@/pages/sponsor/dashboard/SponsorSupportTab';
import { SponsorToolsTab } from '@/pages/sponsor/dashboard/SponsorToolsTab';
import { RoleAiMarketplace } from '@/components/ai/RoleAiMarketplace';
import { useAuth } from '@/lib/auth-context';
import { isApiError, startPlatformCheckout, startProgramCheckout } from '@/lib/api';
import { getSponsorDashboardSummary } from '@/lib/coreApi';
import SponsorSupportDock from '@/pages/sponsor/dashboard/SupportDock';
import { CurrentToolsCard } from '@/components/ai/CurrentToolsCard';

const SLOT_VALUE_CENTS = 80_000;

interface SponsorPublicConfig {
  theme?: string;
  layout?: string;
  hero?: {
    headline?: string | null;
    subheadline?: string | null;
    banner_url?: string | null;
  };
  identity?: {
    org_kind?: string | null;
    location?: string | null;
    industries?: string[];
    short_bio?: string | null;
  };
  focus_tags?: string[];
  top_moments?: Array<{
    id: string;
    title: string;
    kind: string;
    year: number | null;
    summary: string;
    impact?: { classrooms?: number | null; students?: number | null };
    media?: { image_url?: string | null };
    order?: number | null;
    visible?: boolean;
  }>;
  signal_highlights?: Array<{
    id: string;
    label: string;
    value: number | null;
    unit?: string | null;
    note?: string | null;
    trend?: 'up' | 'down' | 'flat' | null;
  }>;
  quote?: {
    text?: string | null;
    person?: string | null;
    title?: string | null;
  };
  social?: {
    website?: string | null;
    linkedin?: string | null;
    x?: string | null;
    instagram?: string | null;
  };
  modules?: {
    show_stats?: boolean;
    show_quote?: boolean;
    show_activity?: boolean;
    show_signal_highlights?: boolean;
    show_moments?: boolean;
  };
  feed_entries?: Array<{
    id: string;
    type: 'story' | 'milestone' | 'digital_good' | 'press';
    title: string;
    summary?: string | null;
    media_url?: string | null;
    cta_text?: string | null;
    cta_url?: string | null;
    tag?: string | null;
    published_at?: string | null;
  }>;
}

interface SponsorSummary {
  sponsor_id: string;
  profile: {
    handle: string | null;
    display_name: string | null;
    tagline: string | null;
    website_url: string | null;
    logo_url: string | null;
    avatar_url?: string | null;
    bio?: string | null;
    is_public?: boolean | null;
    public_config?: SponsorPublicConfig;
    // Legacy fields (for backwards compatibility)
    show_totals?: boolean;
    show_recent_gifts?: boolean;
    show_milestones?: boolean;
    theme?: string | null;
    quote_text?: string | null;
    quote_attribution?: string | null;
    links?: Array<{ id?: string; label?: string; url?: string; icon?: string | null }>;
    feed_entries?: Array<Record<string, unknown>>;
  } | null;
  credits_balance: number;
  program_cents_total: number;
  slots_minted: number;
  next_slot_progress: number;
  next_slot_target: number;
  recent_donations: Array<{
    kind: string;
    amount_cents: number;
    note: string | null;
    created_at: string;
  }>;
  recent_credit_activity: Array<{
    delta_credits: number;
    type: string;
    source: string | null;
    note: string | null;
    created_at: string;
  }>;
}

function formatCurrency(cents: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function formatCredits(credits: number) {
  return `${credits.toLocaleString()} credits`;
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function resolveApiErrorMessage(body: unknown, fallback: string) {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (trimmed) return trimmed;
  }
  if (body && typeof body === 'object') {
    const candidate = (body as Record<string, unknown>).error || (body as Record<string, unknown>).message;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return fallback;
}

function Tabs() {
  const base = '/sponsor/dashboard';
  const tabClasses = 'rounded-full px-3 py-1.5 text-sm transition';
  const active = 'bg-cocoa text-white shadow-sm';
  const idle = 'bg-sand/70 text-cocoa hover:bg-white';

  return (
    <nav className="flex flex-wrap gap-2">
      <NavLink end to={base} className={({ isActive }) => `${tabClasses} ${isActive ? active : idle}`}>
        Overview
      </NavLink>
      <NavLink to={`${base}/profile`} className={({ isActive }) => `${tabClasses} ${isActive ? active : idle}`}>
        Profile
      </NavLink>
      <NavLink to={`${base}/shares`} className={({ isActive }) => `${tabClasses} ${isActive ? active : idle}`}>
        Shares & Gifts
      </NavLink>
      <NavLink to={`${base}/marketplace`} className={({ isActive }) => `${tabClasses} ${isActive ? active : idle}`}>
        Marketplace
      </NavLink>
      <NavLink to={`${base}/tools`} className={({ isActive }) => `${tabClasses} ${isActive ? active : idle}`}>
        Tools
      </NavLink>
      <NavLink to={`${base}/ai-marketplace`} className={({ isActive }) => `${tabClasses} ${isActive ? active : idle}`}>
        AI Marketplace
      </NavLink>
      <NavLink to={`${base}/support`} className={({ isActive }) => `${tabClasses} ${isActive ? active : idle}`}>
        Support
      </NavLink>
      <NavLink to="/portal/sponsor/club" className={({ isActive }) => `${tabClasses} ${isActive ? active : idle}`}>
        The Lounge
      </NavLink>
    </nav>
  );
}

interface OverviewProps {
  summary: SponsorSummary | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function OverviewTab({ summary, loading, error, onRefresh }: OverviewProps) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const location = useLocation();
  const programTotal = Number(summary?.program_cents_total || 0);
  const nextProgress = Number(summary?.next_slot_progress || 0);
  const creditsBalance = Number(summary?.credits_balance || 0);
  const slotsMinted = Number(summary?.slots_minted || 0);
  const [showGiftForm, setShowGiftForm] = useState(false);
  type CheckoutMode = 'fund_program' | 'fund_platform';
  const [checkoutLoading, setCheckoutLoading] = useState<CheckoutMode | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<string | null>(null);
  const [checkoutToast, setCheckoutToast] = useState<string | null>(null);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const cashStatus = searchParams.get('cash_status');
  const shareCodeParam = (searchParams.get('share') || '').trim();
  const shareMode = (searchParams.get('mode') || '').toLowerCase();
  const defaultShareCode = shareCodeParam ? shareCodeParam.toUpperCase() : '';
  const walletLink = defaultShareCode
    ? `/sponsor/wallet?share=${encodeURIComponent(defaultShareCode)}`
    : '/sponsor/wallet';

  useEffect(() => {
    if (!cashStatus) {
      setCheckoutStatus(null);
      return;
    }
    if (cashStatus === 'success') {
      setCheckoutStatus(
        'Thank you for supporting the program! Your contribution helps educators and students across Project Tower.',
      );
    } else if (cashStatus === 'cancel') {
      setCheckoutStatus('Checkout cancelled before completion.');
    } else {
      setCheckoutStatus(null);
    }
  }, [cashStatus]);

  const showCheckoutToast = useCallback(() => {
    setCheckoutToast("Couldn’t start checkout. Please try again.");
  }, []);

  useEffect(() => {
    if (!checkoutToast) return;
    const id = window.setTimeout(() => setCheckoutToast(null), 3200);
    return () => window.clearTimeout(id);
  }, [checkoutToast]);

  const startCheckout = async (mode: CheckoutMode) => {
    setCheckoutError(null);
    const promptLabel =
      mode === 'fund_platform'
        ? 'Enter an amount in USD to support the platform:'
        : 'Enter an amount in USD to support a program:';
    const input = typeof window !== 'undefined' ? window.prompt(promptLabel, '50') : null;
    if (!input) return;

    const value = Number.parseFloat(input);
    if (!Number.isFinite(value) || value <= 0) {
      setCheckoutError('Enter a valid amount (USD).');
      return;
    }

    if (!token) {
      setCheckoutError('Sign in to start a checkout.');
      return;
    }

    setCheckoutLoading(mode);
    const amountCents = Math.round(value * 100);

    try {
      const checkoutFn = mode === 'fund_platform' ? startPlatformCheckout : startProgramCheckout;
      const json = await checkoutFn(amountCents, {
        token,
        successPath: '/sponsor/dashboard?cash_status=success',
        cancelPath: '/sponsor/dashboard?cash_status=cancel',
      });

      const redirectUrl = typeof json?.url === 'string' ? (json.url as string) : null;
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      console.error('[SponsorDashboard] program checkout error', { status: 'ok_missing_url', body: json, reason: 'missing_url' });
      showCheckoutToast();
      setCheckoutError('Couldn’t start checkout. Please try again.');
    } catch (err) {
      if (isApiError(err)) {
        if (err.status === 401) {
          setCheckoutError('Sign in to start a checkout.');
          console.error('[SponsorDashboard] program checkout error', err);
        } else if ([404, 405, 500].includes(err.status)) {
          console.error('[SponsorDashboard] program checkout error', err);
          showCheckoutToast();
          setCheckoutError('Couldn’t start checkout. Please try again.');
        } else {
          const message = resolveApiErrorMessage(err.body, 'Unable to start checkout');
          setCheckoutError(message);
          console.error('[SponsorDashboard] program checkout error', err);
        }
      } else {
        const message = err instanceof Error ? err.message : 'Unable to start checkout';
        setCheckoutError(message);
        console.error('[SponsorDashboard] program checkout error', err);
      }
    } finally {
      setCheckoutLoading(null);
    }
  };

  useEffect(() => {
    setShowGiftForm(false);
  }, [summary?.sponsor_id]);

  useEffect(() => {
    if (shareMode === 'credits' && defaultShareCode) {
      setShowGiftForm(true);
    }
  }, [shareMode, defaultShareCode]);

  if (loading) {
    return <p className="text-cocoa/70">Loading dashboard…</p>;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-brick">{error}</p>
        <button
          type="button"
          className="rounded-lg bg-cocoa px-4 py-2 text-sm font-semibold text-white"
          onClick={onRefresh}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="space-y-2">
        <p className="text-cocoa/70">We could not find your sponsor profile yet.</p>
        <p className="text-sm text-cocoa/60">Create your profile below so teachers can recognize your classroom support.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="space-y-3">
          <h3 className="text-lg font-semibold text-cocoa">Slots</h3>
          <p className="text-sm text-cocoa/70">Program donations mint a classroom slot every $800.</p>
          <p className="text-3xl font-semibold text-cocoa">{slotsMinted}</p>
          <SlotProgress amountCents={nextProgress} targetCents={summary.next_slot_target || SLOT_VALUE_CENTS} />
        </Card>
        <Card className="space-y-3">
          <h3 className="text-lg font-semibold text-cocoa">Program donations</h3>
          <p className="text-sm text-cocoa/70">Total contributed to the platform program.</p>
          <p className="text-3xl font-semibold text-cocoa">{formatCurrency(programTotal)}</p>
        </Card>
        <div className="md:col-span-2 grid gap-4 lg:grid-cols-2">
          <Card className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-cocoa">Support teachers with credit packs or marketplace gifts</h3>
              <p className="text-sm text-cocoa/70">
                Buy teacher gift credit packs or sponsor marketplace items and they will be delivered through Tower credits—no USD lands in
                individual wallets.
              </p>
              <p className="text-2xl font-semibold text-cocoa">{formatCredits(creditsBalance)}</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Link
                to="/sponsor/dashboard/marketplace"
                className="inline-flex justify-center rounded-xl border border-cocoa px-3 py-2 text-xs font-semibold text-cocoa hover:bg-sand/60"
              >
                Shop credit packs &amp; gifts
              </Link>
              <Link
                to={walletLink}
                className="inline-flex justify-center rounded-xl border border-cocoa px-3 py-2 text-xs font-semibold text-cocoa hover:bg-sand/60"
              >
                View wallet &amp; activity
              </Link>
            </div>
            <button
              type="button"
              className="w-full rounded-xl bg-cocoa px-4 py-2 text-sm font-semibold text-white shadow-sm"
              onClick={() => setShowGiftForm((prev) => !prev)}
            >
              {showGiftForm ? 'Hide credit gift form' : 'Gift existing credits'}
            </button>
            {showGiftForm && <GiftingForm defaultShareCode={defaultShareCode} onSuccess={() => onRefresh()} />}
          </Card>
          <Card className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-cocoa">Support a program</h3>
              <p className="text-sm text-cocoa/70">
                Cash donations power the Tower platform program and mint classroom slots every $800.
              </p>
            </div>
            <button
              type="button"
              className="w-full rounded-xl border border-cocoa px-4 py-2 text-sm font-semibold text-cocoa shadow-sm transition hover:border-cocoa/70 disabled:cursor-not-allowed disabled:border-sand"
              onClick={() => startCheckout('fund_program')}
              disabled={checkoutLoading === 'fund_program'}
            >
              {checkoutLoading === 'fund_program' ? 'Starting program checkout…' : 'Start program checkout'}
            </button>
            <button
              type="button"
              className="w-full rounded-xl bg-cocoa px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-cocoa/90 disabled:cursor-not-allowed disabled:bg-cocoa/60"
              onClick={() => startCheckout('fund_platform')}
              disabled={checkoutLoading === 'fund_platform'}
            >
              {checkoutLoading === 'fund_platform' ? 'Starting platform checkout…' : 'Support the platform'}
            </button>
            {checkoutError ? <p className="text-sm text-brick">{checkoutError}</p> : null}
            {checkoutStatus ? <p className="text-sm text-mint-700">{checkoutStatus}</p> : null}
          </Card>
        </div>
      </div>

      <CurrentToolsCard role="sponsor" manageHref="/sponsor/dashboard/ai-marketplace" />

      {checkoutToast ? (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-[200] -translate-x-1/2 rounded-full bg-cocoa px-4 py-2 text-sm font-medium text-white shadow-lg">
          {checkoutToast}
        </div>
      ) : null}

    </div>
  );
}

interface SharesProps {
  summary: SponsorSummary | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function SharesTab({ summary, loading, error, onRefresh }: SharesProps) {
  if (loading) {
    return <p className="text-cocoa/70">Loading recent activity…</p>;
  }

  if (error) {
    return (
      <div className="space-y-3">
        <p className="text-brick">{error}</p>
        <button
          type="button"
          className="rounded-lg bg-cocoa px-4 py-2 text-sm font-semibold text-white"
          onClick={onRefresh}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!summary) {
    return <p className="text-cocoa/60">No sponsor activity yet. Start by funding a classroom slot.</p>;
  }

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-cocoa">Recent donations</h3>
        {summary.recent_donations.length === 0 ? (
          <p className="text-sm text-cocoa/60">Donations will appear here once you support the platform program.</p>
        ) : (
          <ul className="space-y-3">
            {summary.recent_donations.map((donation, index) => (
              <li key={`${donation.created_at}-${index}`} className="rounded-2xl bg-white/70 p-4 shadow-soft">
                <p className="text-sm font-medium text-cocoa">
                  {formatDate(donation.created_at)} ·
                  {donation.kind === 'platform_program' || donation.kind === 'program'
                    ? 'Program donation'
                    : donation.kind === 'platform'
                      ? 'Platform funding'
                      : donation.kind === 'ops'
                        ? 'Operations support'
                        : donation.kind?.includes('prospect')
                          ? 'Prospect support'
                          : 'Donation'}
                </p>
                <p className="text-sm text-cocoa/80">{formatCurrency(donation.amount_cents)}</p>
                <p className="text-xs text-cocoa/60">{donation.note || 'Logged via dashboard'}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-cocoa">Recent credit activity</h3>
        {summary.recent_credit_activity.length === 0 ? (
          <p className="text-sm text-cocoa/60">Gift credits to teachers to see the activity timeline.</p>
        ) : (
          <ul className="space-y-3">
            {summary.recent_credit_activity.map((entry, index) => (
              <li key={`${entry.created_at}-${index}`} className="rounded-2xl bg-white/70 p-4 shadow-soft">
                <p className="text-sm font-medium text-cocoa">
                  {formatDate(entry.created_at)} · {entry.type}
                </p>
                <p className="text-sm text-cocoa/80">{formatCredits(Math.abs(entry.delta_credits))}</p>
                <p className="text-xs text-cocoa/60">{entry.note || 'Activity logged via dashboard'}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default function SponsorDashboard() {
  const { session } = useAuth();
  const token = session?.access_token || null;
  const location = useLocation();
  const [summary, setSummary] = useState<SponsorSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(() => {
    if (!token) {
      setSummary(null);
      setLoading(false);
      setError('Sign in as a sponsor to view your dashboard.');
      return;
    }

    setLoading(true);
    setError(null);

    getSponsorDashboardSummary()
      .then((result) => {
        setSummary(result.data as unknown as SponsorSummary);
        setError(null);
      })
      .catch((err) => {
        setSummary(null);
        setError(err?.message || 'Unable to load sponsor dashboard');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const greeting = useMemo(() => {
    const name = summary?.profile?.display_name || session?.user?.email?.split('@')[0] || 'Sponsor';
    return `Welcome back, ${name}!`;
  }, [summary?.profile?.display_name, session?.user?.email]);

  const inSupportTab = location.pathname.startsWith('/sponsor/dashboard/support');

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Sponsor dashboard</h1>
        <p className="text-sm text-cocoa/70">{greeting}</p>
      </header>

      <Tabs />

      <Routes>
        <Route
          index
          element={<OverviewTab summary={summary} loading={loading} error={error} onRefresh={loadSummary} />}
        />
        <Route
          path="profile"
          element={<SponsorProfileDashboardProfile token={token} profile={summary?.profile ?? null} onSaved={loadSummary} />}
        />
        <Route
          path="shares"
          element={<SharesTab summary={summary} loading={loading} error={error} onRefresh={loadSummary} />}
        />
        <Route path="marketplace" element={<SponsorMarketplace />} />
        <Route path="tools" element={<SponsorToolsTab />} />
        <Route
          path="ai-marketplace"
          element={
            <RoleAiMarketplace
              role="sponsor"
              title="AI Tools Marketplace"
              description="Browse and activate AI tools to enhance your sponsorship workflow. Choose up to 4 tools — first activation costs credits, then they're yours."
            />
          }
        />
        <Route path="support" element={<SponsorSupportTab />} />
      </Routes>

      {!inSupportTab && <SponsorSupportDock />}
    </div>
  );
}
